import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'react-hot-toast';
import { BarChart3, Play, Plus, Trash2, Save, Download, FolderOpen, RefreshCw } from 'lucide-react';
import { reportsApi } from '../services/reportsApi';
import DataTable from '../components/common/DataTable';

const OPERATORS = [
  { value: 'eq', label: 'equals' },
  { value: 'ne', label: 'not equals' },
  { value: 'gt', label: 'greater than' },
  { value: 'gte', label: 'greater or equal' },
  { value: 'lt', label: 'less than' },
  { value: 'lte', label: 'less or equal' },
  { value: 'contains', label: 'contains' },
  { value: 'in', label: 'in (comma list)' },
  { value: 'between', label: 'between' },
  { value: 'is_empty', label: 'is empty' },
  { value: 'is_not_empty', label: 'is not empty' },
];
const NO_VALUE_OPS = ['is_empty', 'is_not_empty'];

const ReportBuilder = () => {
  const [doctypes, setDoctypes] = useState([]);
  const [doctype, setDoctype] = useState('');
  const [fields, setFields] = useState([]);
  const [selectedCols, setSelectedCols] = useState([]);
  const [filters, setFilters] = useState([]);
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const [groupBy, setGroupBy] = useState('');
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [savedReports, setSavedReports] = useState([]);
  const [reportName, setReportName] = useState('');
  const [activeReportId, setActiveReportId] = useState(null);

  useEffect(() => {
    reportsApi.doctypes().then(r => setDoctypes(r.data || [])).catch(() => toast.error('Failed to load doctypes'));
    loadSaved();
  }, []);

  const loadSaved = () => {
    reportsApi.list().then(r => setSavedReports(r.data || [])).catch(() => {});
  };

  const loadFields = useCallback(async (dt) => {
    try {
      const r = await reportsApi.fields(dt);
      setFields(r.data || []);
      return r.data || [];
    } catch {
      toast.error('Failed to load fields');
      return [];
    }
  }, []);

  const handleDoctypeChange = async (dt) => {
    setDoctype(dt);
    setResult(null);
    setActiveReportId(null);
    setFilters([]);
    setSortField('');
    setGroupBy('');
    const f = await loadFields(dt);
    setSelectedCols(f.map(x => x.fieldname)); // default: all columns
  };

  const toggleCol = (fieldname) => {
    setSelectedCols(prev =>
      prev.includes(fieldname) ? prev.filter(c => c !== fieldname) : [...prev, fieldname]
    );
  };

  const addFilter = () => setFilters(prev => [...prev, { field: fields[0]?.fieldname || '', operator: 'eq', value: '', value2: '' }]);
  const updateFilter = (idx, patch) => setFilters(prev => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  const removeFilter = (idx) => setFilters(prev => prev.filter((_, i) => i !== idx));

  const buildQuery = () => ({
    doctype,
    columns: groupBy ? [] : selectedCols,
    filters: filters
      .filter(f => f.field && (NO_VALUE_OPS.includes(f.operator) || f.value !== ''))
      .map(f => ({ field: f.field, operator: f.operator, value: NO_VALUE_OPS.includes(f.operator) ? null : f.value, value2: f.operator === 'between' ? f.value2 : null })),
    sort: sortField ? [{ field: sortField, direction: sortDir }] : [],
    group_by: groupBy || null,
    limit: 500,
  });

  const runReport = async () => {
    if (!doctype) { toast.error('Select a DocType first'); return; }
    setRunning(true);
    try {
      const r = await reportsApi.run(buildQuery());
      setResult(r.data);
      toast.success(`${r.data.total} row(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to run report');
    } finally {
      setRunning(false);
    }
  };

  const saveReport = async () => {
    if (!reportName.trim()) { toast.error('Enter a report name'); return; }
    if (!doctype) { toast.error('Select a DocType'); return; }
    try {
      const payload = { ...buildQuery(), name: reportName.trim() };
      if (activeReportId) {
        await reportsApi.update(activeReportId, payload);
        toast.success('Report updated');
      } else {
        const r = await reportsApi.create(payload);
        setActiveReportId(r.data.report_id);
        toast.success('Report saved');
      }
      loadSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save report');
    }
  };

  const loadReport = async (rep) => {
    setActiveReportId(rep.report_id);
    setReportName(rep.name);
    setDoctype(rep.doctype);
    const f = await loadFields(rep.doctype);
    setSelectedCols(rep.columns?.length ? rep.columns : f.map(x => x.fieldname));
    setFilters((rep.filters || []).map(x => ({ field: x.field, operator: x.operator, value: x.value ?? '', value2: x.value2 ?? '' })));
    setSortField(rep.sort?.[0]?.field || '');
    setSortDir(rep.sort?.[0]?.direction || 'asc');
    setGroupBy(rep.group_by || '');
    try {
      const r = await reportsApi.runSaved(rep.report_id);
      setResult(r.data);
    } catch {
      toast.error('Saved report could not run — its DocType or fields may have changed');
    }
  };

  const deleteReport = async (id, e) => {
    e.stopPropagation();
    try {
      await reportsApi.remove(id);
      toast.success('Report deleted');
      if (id === activeReportId) { setActiveReportId(null); setReportName(''); }
      loadSaved();
    } catch { toast.error('Failed to delete'); }
  };

  const exportCsv = () => {
    if (!result?.rows?.length) { toast.error('Run a report first'); return; }
    const cols = result.columns.map(c => c.fieldname);
    const header = result.columns.map(c => `"${c.label}"`).join(',');
    const lines = result.rows.map(r => cols.map(c => {
      const v = r[c] ?? '';
      return `"${String(v).replace(/"/g, '""')}"`;
    }).join(','));
    const csv = [header, ...lines].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${reportName || doctype || 'report'}.csv`;
    a.click();
  };

  const resultColumns = (result?.columns || []).map(c => ({
    key: c.fieldname,
    header: c.label,
    accessor: (row) => row[c.fieldname],
    align: c.fieldtype === 'number' ? 'right' : 'left',
    filterable: true,
  }));

  return (
    <Layout>
      <div className="p-6 max-w-[1600px] mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Report Builder</h1>
              <p className="text-sm text-gray-500">Build, save and export reports across any DocType</p>
            </div>
          </div>
          <Button variant="outline" onClick={loadSaved}><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Builder panel */}
          <Card className="lg:col-span-1 p-4 space-y-4 h-fit" data-testid="report-builder-panel">
            <div>
              <Label className="text-xs font-semibold">DocType</Label>
              <Select value={doctype} onValueChange={handleDoctypeChange}>
                <SelectTrigger data-testid="report-doctype-select"><SelectValue placeholder="Select a DocType" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {doctypes.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {fields.length > 0 && (
              <>
                <div>
                  <Label className="text-xs font-semibold">Columns</Label>
                  <div className="mt-1 max-h-44 overflow-y-auto border rounded-md p-2 space-y-1">
                    {fields.map(f => (
                      <label key={f.fieldname} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 px-1 py-0.5 rounded">
                        <Checkbox
                          checked={selectedCols.includes(f.fieldname)}
                          onCheckedChange={() => toggleCol(f.fieldname)}
                          disabled={!!groupBy}
                          data-testid={`report-col-${f.fieldname}`}
                        />
                        <span className="truncate">{f.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-xs font-semibold">Filters</Label>
                    <Button size="sm" variant="ghost" onClick={addFilter} data-testid="add-filter-btn"><Plus className="w-3.5 h-3.5" /></Button>
                  </div>
                  <div className="space-y-2">
                    {filters.map((f, idx) => (
                      <div key={idx} className="border rounded-md p-2 space-y-1.5 bg-gray-50">
                        <div className="flex gap-1.5">
                          <Select value={f.field} onValueChange={(v) => updateFilter(idx, { field: v })}>
                            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent className="max-h-60">
                              {fields.map(fl => <SelectItem key={fl.fieldname} value={fl.fieldname}>{fl.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => removeFilter(idx)}>
                            <Trash2 className="w-3.5 h-3.5 text-red-500" />
                          </Button>
                        </div>
                        <Select value={f.operator} onValueChange={(v) => updateFilter(idx, { operator: v })}>
                          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {OPERATORS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        {!NO_VALUE_OPS.includes(f.operator) && (
                          <Input className="h-8 text-xs" placeholder="Value" value={f.value} onChange={(e) => updateFilter(idx, { value: e.target.value })} />
                        )}
                        {f.operator === 'between' && (
                          <Input className="h-8 text-xs" placeholder="And value" value={f.value2} onChange={(e) => updateFilter(idx, { value2: e.target.value })} />
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-xs font-semibold">Sort By</Label>
                  <div className="flex gap-1.5 mt-1">
                    <Select value={sortField || '__none__'} onValueChange={(v) => setSortField(v === '__none__' ? '' : v)}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="None" /></SelectTrigger>
                      <SelectContent className="max-h-60">
                        <SelectItem value="__none__">None</SelectItem>
                        {fields.map(fl => <SelectItem key={fl.fieldname} value={fl.fieldname}>{fl.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={sortDir} onValueChange={setSortDir}>
                      <SelectTrigger className="h-8 text-xs w-24"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="asc">Asc</SelectItem>
                        <SelectItem value="desc">Desc</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="text-xs font-semibold">Group By (count)</Label>
                  <Select value={groupBy || '__none__'} onValueChange={(v) => setGroupBy(v === '__none__' ? '' : v)}>
                    <SelectTrigger className="h-8 text-xs mt-1" data-testid="report-groupby-select"><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent className="max-h-60">
                      <SelectItem value="__none__">None</SelectItem>
                      {fields.map(fl => <SelectItem key={fl.fieldname} value={fl.fieldname}>{fl.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <Button className="w-full bg-orange-600 hover:bg-orange-700" onClick={runReport} disabled={running} data-testid="run-report-btn">
                  <Play className="w-4 h-4 mr-2" />{running ? 'Running…' : 'Run Report'}
                </Button>

                <div className="border-t pt-3 space-y-2">
                  <Input placeholder="Report name" value={reportName} onChange={(e) => setReportName(e.target.value)} className="h-8 text-sm" data-testid="report-name-input" />
                  <div className="flex gap-2">
                    <Button variant="outline" className="flex-1" onClick={saveReport} data-testid="save-report-btn"><Save className="w-4 h-4 mr-1" />{activeReportId ? 'Update' : 'Save'}</Button>
                    <Button variant="outline" className="flex-1" onClick={exportCsv}><Download className="w-4 h-4 mr-1" />CSV</Button>
                  </div>
                </div>
              </>
            )}
          </Card>

          {/* Results + saved reports */}
          <div className="lg:col-span-3 space-y-6">
            {savedReports.length > 0 && (
              <Card className="p-4">
                <Label className="text-xs font-semibold flex items-center gap-1 mb-2"><FolderOpen className="w-3.5 h-3.5" />Saved Reports</Label>
                <div className="flex flex-wrap gap-2">
                  {savedReports.map(rep => (
                    <button
                      key={rep.report_id}
                      onClick={() => loadReport(rep)}
                      data-testid={`saved-report-${rep.report_id}`}
                      className={`group flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm transition-colors ${activeReportId === rep.report_id ? 'border-orange-400 bg-orange-50 text-orange-700' : 'border-gray-200 hover:border-orange-300'}`}
                    >
                      {rep.name} <span className="text-xs text-gray-400">({rep.doctype})</span>
                      <Trash2 className="w-3.5 h-3.5 text-gray-300 group-hover:text-red-500" onClick={(e) => deleteReport(rep.report_id, e)} />
                    </button>
                  ))}
                </div>
              </Card>
            )}

            <Card className="p-5" data-testid="report-results">
              {result ? (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-800">Results {result.grouped && <span className="text-xs text-orange-600">(grouped)</span>}</h3>
                    <span className="text-sm text-gray-500">{result.total} rows</span>
                  </div>
                  <DataTable
                    testid="report-result-table"
                    columns={resultColumns}
                    data={result.rows}
                    emptyText="No matching records"
                  />
                </>
              ) : (
                <div className="h-64 flex flex-col items-center justify-center text-gray-400">
                  <BarChart3 className="w-10 h-10 mb-2" />
                  <p className="text-sm">Select a DocType and click "Run Report"</p>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ReportBuilder;
